<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Edit Procedure</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.name"
                outlined
                label="Procedure Name *"
                prepend-inner-icon="mdi-clipboard-text-outline"
                :error-messages="errors.name"
                placeholder="e.g., Walking Exercise, Running Protocol"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.default_speed"
                outlined
                label="Default Speed (mph)"
                prepend-inner-icon="mdi-speedometer"
                type="number"
                step="0.1"
                min="0"
                max="25"
                :error-messages="errors.default_speed"
                placeholder="e.g., 3.5"
              ></v-text-field>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.description"
                outlined
                label="Description"
                prepend-inner-icon="mdi-text"
                rows="3"
                :error-messages="errors.description"
                placeholder="Detailed description of the procedure, its purpose and expected outcomes..."
              ></v-textarea>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.max_speed"
                outlined
                label="Maximum Speed (mph)"
                prepend-inner-icon="mdi-speedometer-medium"
                type="number"
                step="0.1"
                min="0"
                max="25"
                :error-messages="errors.max_speed"
                placeholder="e.g., 8.0"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.default_unweighting"
                outlined
                label="Default Unweighting (%)"
                prepend-inner-icon="mdi-weight"
                type="number"
                step="0.1"
                min="0"
                max="100"
                :error-messages="errors.default_unweighting"
                placeholder="e.g., 50.0"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.max_heart_rate"
                outlined
                label="Maximum Heart Rate (bpm)"
                prepend-inner-icon="mdi-heart-pulse"
                type="number"
                min="60"
                max="220"
                :error-messages="errors.max_heart_rate"
                placeholder="e.g., 150"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-switch
                v-model="form.is_active"
                label="Active"
                color="primary"
                :false-value="false"
                :true-value="true"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Procedure Information Card -->
          <v-row v-if="procedure">
            <v-col sm="12">
              <v-card outlined class="pa-4 mt-4">
                <v-card-title class="text-h6">Procedure Information</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col sm="4">
                      <p><strong>Created By:</strong></p>
                      <p>{{ procedure.created_by ? procedure.created_by.first_name + ' ' + procedure.created_by.last_name : 'Unknown' }}</p>
                    </v-col>
                    <v-col sm="4">
                      <p><strong>Created At:</strong></p>
                      <p>{{ formatDate(procedure.created_at) }}</p>
                    </v-col>
                    <v-col sm="4">
                      <p><strong>Last Updated:</strong></p>
                      <p>{{ formatDate(procedure.updated_at) }}</p>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Preview Card -->
          <v-row v-if="form.name || form.description">
            <v-col sm="12">
              <v-card outlined class="pa-4 mt-4">
                <v-card-title class="text-h6">Current Settings Preview</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col sm="6">
                      <p><strong>Name:</strong> {{ form.name || 'Not specified' }}</p>
                      <p><strong>Description:</strong> {{ form.description || 'Not specified' }}</p>
                      <p><strong>Speed Range:</strong> {{ formatSpeedRange() }}</p>
                    </v-col>
                    <v-col sm="6">
                      <p><strong>Max Heart Rate:</strong> {{ form.max_heart_rate ? form.max_heart_rate + ' bpm' : 'Not specified' }}</p>
                      <p><strong>Unweighting:</strong> {{ form.default_unweighting ? form.default_unweighting + '%' : 'Not specified' }}</p>
                      <p><strong>Intensity Level:</strong> {{ getIntensityLevel() }}</p>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col sm="12">
                      <v-chip 
                        small 
                        :color="form.is_active ? 'green' : 'red'" 
                        text-color="white"
                      >
                        {{ form.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                      <v-chip 
                        v-if="isSuitableForRehabilitation()" 
                        small 
                        color="blue" 
                        text-color="white"
                        class="ml-2"
                      >
                        Rehabilitation Suitable
                      </v-chip>
                      <v-chip 
                        v-if="isSuitableForAthletic()" 
                        small 
                        color="purple" 
                        text-color="white"
                        class="ml-2"
                      >
                        Athletic Suitable
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col sm="12" class="text-right">
              <v-btn
                :loading="isLoading"
                :disabled="isLoading"
                rounded
                color="primary"
                dark
                @click="save"
                class="mr-2"
              >
                Update Procedure
              </v-btn>
              <v-btn
                outlined
                color="error"
                rounded
                @click="deleteProcedure"
                :disabled="isDeleting"
                :loading="isDeleting"
                class="mr-2"
              >
                Delete Procedure
              </v-btn>
              <v-btn
                outlined
                color="secondary"
                rounded
                @click="resetForm"
              >
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="500">
        <v-card rounded="xxl">
          <v-card-title class="headline">Confirm Delete</v-card-title>
          <v-card-text>
            Are you sure you want to delete this procedure? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="isDeleting">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "ProcedureEditPage",
  data() {
    return {
      form: {
        is_active: true,
        name: "",
        description: "",
        default_speed: "",
        max_speed: "",
        default_unweighting: "",
        max_heart_rate: "",
      },
      procedure: null,
      originalForm: {},
      isLoading: false,
      isDeleting: false,
      showDeleteDialog: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters['errors/getErrors'];
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      try {
        const response = await axios.get(`/procedures/${this.$route.params.id}`);
        this.procedure = response.data.data;
        this.form = {
          is_active: this.procedure.is_active,
          name: this.procedure.name || "",
          description: this.procedure.description || "",
          default_speed: this.procedure.default_speed || "",
          max_speed: this.procedure.max_speed || "",
          default_unweighting: this.procedure.default_unweighting || "",
          max_heart_rate: this.procedure.max_heart_rate || "",
        };
        // Keep a copy of original form for reset functionality
        this.originalForm = { ...this.form };
      } catch (error) {
        console.error("Error fetching procedure:", error);
        if (error.response && error.response.status === 404) {
          this.$router.push("/procedures");
        } else if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      }
    },
    async save() {
      try {
        this.isLoading = true;
        await axios.put(`/procedures/${this.$route.params.id}`, this.form);
        this.isLoading = false;
        this.$router.push("/procedures");
      } catch (e) {
        console.error("Error updating procedure:", e);
        this.isLoading = false;
      }
    },
    deleteProcedure() {
      this.showDeleteDialog = true;
    },
    async confirmDelete() {
      try {
        this.isDeleting = true;
        await axios.post(`/procedures/delete/${this.$route.params.id}`);
        this.isDeleting = false;
        this.showDeleteDialog = false;
        this.$router.push("/procedures");
      } catch (error) {
        console.error("Error deleting procedure:", error);
        this.isDeleting = false;
      }
    },
    resetForm() {
      this.form = { ...this.originalForm };
    },
    formatSpeedRange() {
      const defaultSpeed = this.form.default_speed;
      const maxSpeed = this.form.max_speed;
      
      if (!defaultSpeed && !maxSpeed) {
        return 'Not specified';
      } else if (defaultSpeed && !maxSpeed) {
        return defaultSpeed + ' mph (default)';
      } else if (!defaultSpeed && maxSpeed) {
        return 'Up to ' + maxSpeed + ' mph';
      } else {
        return defaultSpeed + ' - ' + maxSpeed + ' mph';
      }
    },
    getIntensityLevel() {
      const speed = parseFloat(this.form.default_speed) || 0;
      const heartRate = parseInt(this.form.max_heart_rate) || 0;

      if (speed <= 2.0 && heartRate <= 120) {
        return 'Low';
      } else if (speed <= 5.0 && heartRate <= 150) {
        return 'Moderate';
      } else if (speed <= 8.0 && heartRate <= 170) {
        return 'High';
      } else {
        return 'Very High';
      }
    },
    isSuitableForRehabilitation() {
      const speed = parseFloat(this.form.default_speed) || null;
      const unweighting = parseFloat(this.form.default_unweighting) || null;
      return (speed === null || speed <= 3.0) && (unweighting === null || unweighting >= 50.0);
    },
    isSuitableForAthletic() {
      const defaultSpeed = parseFloat(this.form.default_speed) || null;
      const maxSpeed = parseFloat(this.form.max_speed) || null;
      return (defaultSpeed !== null && defaultSpeed >= 6.0) || (maxSpeed !== null && maxSpeed >= 8.0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
    }
  },
};
</script>